import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '@/redux';
import { clearCredentials, AuthUser } from '@/redux/slices/authSlice';
import { clearPlan } from '@/redux/slices/planSlice';
import { onboardingActions } from '@/redux/slices/onboardingSlice';
import { authService } from '@/services/auth.service';
import { ROUTES } from '@/constants/routes';

export type { AuthUser };
export type UserRole = 'owner' | 'manager' | 'cashier' | 'inventory' | 'viewer';

/**
 * @deprecated Use useRbac().hasPermission() instead of role hierarchy checks.
 * Kept for backward compatibility with isOwner convenience checks only.
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  owner: 5,
  manager: 4,
  cashier: 2,
  inventory: 2,
  viewer: 1,
};

/**
 * @deprecated Use useRbac() permission keys instead.
 * Feature-based role mapping is not flexible for custom roles.
 */
const FEATURE_PERMISSIONS: Record<string, UserRole[]> = {
  pos: ['owner', 'manager', 'cashier'],
  inventory: ['owner', 'manager', 'inventory'],
  reports: ['owner', 'manager', 'viewer'],
  staff: ['owner', 'manager'],
  settings: ['owner'],
  orders: ['owner', 'manager', 'cashier', 'inventory', 'viewer'],
  products: ['owner', 'manager', 'inventory'],
  plan: ['owner'],
};

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user) as AuthUser | null;
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const logoutUser = useCallback(async () => {
    try { await authService.logout(); } catch { /* ignore */ }
    // clearCredentials đã xoá POS shift binding (pos.shiftStoreId + tenant tag) —
    // single source-of-truth, bao phủ cả logout lẫn token-expiry/401.
    dispatch(clearCredentials());
    dispatch(clearPlan());
    dispatch(onboardingActions.reset());
    navigate(ROUTES.auth.login, { replace: true });
  }, [dispatch, navigate]);

  // Role checking functions
  const hasRole = useCallback(
    (requiredRoles: UserRole[]) => {
      if (!user) return false;
      return requiredRoles.includes(user.role);
    },
    [user]
  );

  /** @deprecated Use useRbac().hasPermission() instead. */
  const hasMinimumRole = useCallback(
    (minimumRole: UserRole) => {
      if (!user) return false;
      return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
    },
    [user]
  );

  /** @deprecated Use useRbac().hasPermission() with specific permission keys. */
  const canAccess = useCallback(
    (feature: keyof typeof FEATURE_PERMISSIONS) => {
      if (!user) return false;
      const requiredRoles = FEATURE_PERMISSIONS[feature];
      if (!requiredRoles) return false;
      return requiredRoles.includes(user.role);
    },
    [user]
  );

  /**
   * UI-only convenience booleans. For security checks use useRbac().hasPermission().
   * @deprecated Use useRbac().hasPermission() instead.
   */
  const canManageUsers = useMemo(() => hasRole(['owner', 'manager']), [hasRole]);
  /** @deprecated Use useRbac().hasPermission('inventory.*') instead. */
  const canManageInventory = useMemo(() => hasRole(['owner', 'manager', 'inventory']), [hasRole]);
  /** @deprecated Use useRbac().hasPermission('pos.use') instead. */
  const canUsePOS = useMemo(() => hasRole(['owner', 'manager', 'cashier']), [hasRole]);
  /** @deprecated Use useRbac().hasPermission('reports.revenue') instead. */
  const canViewReports = useMemo(() => hasRole(['owner', 'manager', 'viewer']), [hasRole]);
  /** @deprecated Use useRbac().hasPermission('settings.*') instead. */
  const canManageSettings = useMemo(() => hasRole(['owner']), [hasRole]);
  const isOwner = useMemo(() => user?.role === 'owner', [user]);

  return {
    user,
    isAuthenticated,
    logout: logoutUser,
    hasRole,
    hasMinimumRole,
    canAccess,
    canManageUsers,
    canManageInventory,
    canUsePOS,
    canViewReports,
    canManageSettings,
    isOwner,
  };
};
